import React, { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthModalContext } from "../../../contexts/AuthModalContext";
import "../../../assets/css/style.css";

// components
import Head from "../../general/Head";
import OnBoardingCompleted from "../../screens/onboarding-screens/OnBoardingCompleted";
import OnboardingFeelToday from "../../screens/onboarding-screens/OnboardingFeelToday";
import OnBoardingPerson from "../../screens/onboarding-screens/OnBoardingPerson";
import OnBoardingPersonAge from "../../screens/onboarding-screens/OnBoardingPersonAge";
import OnBoardingPersonFeelings from "../../screens/onboarding-screens/OnBoardingPersonFeelings";
import OnBoardingPersonGender from "../../screens/onboarding-screens/OnBoardingPersonGender";
import OnBoardingPersonMeditate from "../../screens/onboarding-screens/OnBoardingPersonMeditate";
import OnBoardingPersonName from "../../screens/onboarding-screens/OnBoardingPersonName";
import HomeRouter from "./HomeRouter";
import TermsAndCondition from "../../screens/dashboard/account/TermsAndCondition";
import PrivacyPolicy from "../../screens/dashboard/account/PrivacyPolicy";
import LandingPage from "../../landing/LandingPage";
import DashboardHeader from "../../general/DashboardHeader";
import LandingFooter from "../../landing/LandingFooter";
import About from "../../screens/dashboard/account/About";
import ConcernsPage from "../../landing/ConcernsPage";
import ConcernDetailPage from "../../landing/ConcernDetailPage";
import CreateSessionSuccessModal from "../../includes/modals/CreateSessionSuccessModal";
import { Context } from "../../../contexts/Store";
import { PatientConfig, socketUrl } from "../../../axiosConfig";

export default function AppRouter() {
  const { state, dispatch } = useContext(Context);
  const userId = state.user_details?.user_id;
  const access_token = state.user_details?.access_token;
  const isPaymentPending = state.user_details?.ipp;
  const location = useLocation();

  // Notifications (for single header)
  const [unreadNotifictaionsCount, setUnreadNotificationsCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isExistingSession, setIsExistingSession] = useState(false);
  const [acceptedSessionData, setAcceptedSessionData] = useState([]);
  const [acceptedSessionIds, setAcceptedSessionIds] = useState([]);
  const [isAcceptedModalOpen, setIsAcceptedModalOpen] = useState(false);
  const [isASessionAccepted, setIsASessionAccepted] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalInitialPage, setLoginModalInitialPage] = useState("login");
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const sessionDetails = localStorage.getItem("session_details");
      const new_session = localStorage.getItem("new_session");
      if (sessionDetails) {
        const parsed = JSON.parse(sessionDetails);
        dispatch({ type: "UPDATE_SESSION_DETAILS", new_session: parsed });
      }
      if (new_session) {
        const parsed = JSON.parse(new_session);
        dispatch({ type: "ADD_SESSION", new_session: parsed });
      }
    } catch (_) {}
  }, [dispatch]);

  const getNotifications = () => {
    if (!access_token) return;
    PatientConfig.get("api/v1/general/notifications/", {
      headers: { Authorization: `Bearer ${access_token}` },
    })
      .then((response) => {
        const { StatusCode, data, existing_session, unread_notifications_count } = response.data;
        if (StatusCode === 6000) {
          setIsExistingSession(!!existing_session);
          setUnreadNotificationsCount(unread_notifications_count || 0);
          setUnreadNotifications(data?.filter((n) => !n.read) || []);
          setNotifications(data || []);
          const unreadAccepted = (data || []).filter(
            (n) => !n.read && n.notification_type === "Session accepted"
          );
          if (unreadAccepted.length > 0) {
            setAcceptedSessionIds(unreadAccepted.map((n) => n.id));
            setAcceptedSessionData(unreadAccepted.map((n) => n.extra_data));
            setIsAcceptedModalOpen(true);
            setIsASessionAccepted(true);
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getNotifications();
  }, [access_token]);

  useEffect(() => {
    if (!userId) return;
    const socket = new WebSocket(`${socketUrl}/notifications/${userId}/`);
    socket.onmessage = () => getNotifications();
    socket.onerror = () => {};
    return () => socket.close();
  }, [userId]);

  // Show single header for main app routes (hide for onboarding, auth, etc.)
  const showHeader =
    location.pathname === "/" ||
    location.pathname === "/about-us" ||
    location.pathname === "/concerns" ||
    location.pathname.startsWith("/concerns/") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/describe") ||
    location.pathname.startsWith("/schedule") ||
    location.pathname.startsWith("/fulfill") ||
    location.pathname.startsWith("/create-session") ||
    location.pathname === "/session-confirmed" ||
    location.pathname.startsWith("/notifications") ||
    location.pathname.startsWith("/session/") ||
    location.pathname.startsWith("/view-summary-report/") ||
    location.pathname.startsWith("/cancel-session/") ||
    location.pathname.startsWith("/payment/") ||
    location.pathname === "/account" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-and-conditions" ||
    location.pathname === "/refund-policy" ||
    location.pathname === "/faq" ||
    location.pathname === "/transaction-history" ||
    location.pathname === "/meditate" ||
    location.pathname === "/explore" ||
    location.pathname === "/statistics" ||
    location.pathname === "/video" ||
    location.pathname === "/more";

  // Open login/register modal when landing on / with ?auth=login or ?auth=signup
  useEffect(() => {
    if (location.pathname !== "/" || !location.search) return;
    const params = new URLSearchParams(location.search);
    const auth = params.get("auth");
    if (auth === "login" || auth === "signup") {
      setLoginModalOpen(true);
      setLoginModalInitialPage(auth);
      params.delete("auth");
      const next = params.get("next");
      const newSearch = params.toString();
      const newPath = newSearch ? `/?${newSearch}` : "/";
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const openLoginModal = (initialPage = "login") => {
    setLoginModalInitialPage(initialPage);
    setLoginModalOpen(true);
  };

  const authModalValue = { openLoginModal };

  // When modal closes, reset initial page to login for next open
  useEffect(() => {
    if (!loginModalOpen) setLoginModalInitialPage("login");
  }, [loginModalOpen]);

  return (
    <AuthModalContext.Provider value={authModalValue}>
      {showHeader && (
        <DashboardHeader
          getHeaderNotifications={getNotifications}
          unreadNotifictaionsCount={unreadNotifictaionsCount}
          unreadNotifications={unreadNotifications}
          loginModalOpen={loginModalOpen}
          setLoginModalOpen={setLoginModalOpen}
          loginModalInitialPage={loginModalInitialPage}
        />
      )}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/about-us"
          element={
            <React.Fragment>
              <About />
              <LandingFooter />
            </React.Fragment>
          }
        />
        <Route path="/concerns" element={<ConcernsPage />} />
        <Route path="/concerns/:id" element={<ConcernDetailPage />} />
        <Route path="/onboarding-person" element={[<Head />, <OnBoardingPerson />]} />
        <Route path="/onboarding-person-name" element={[<Head />, <OnBoardingPersonName />]} />
        <Route path="/onboarding-person-age" element={[<Head />, <OnBoardingPersonAge />]} />
        <Route path="/onboarding-person-gender" element={[<Head />, <OnBoardingPersonGender />]} />
        <Route path="/onboarding-person-feelings" element={[<Head />, <OnBoardingPersonFeelings />]} />
        <Route path="/onboarding-person-meditate" element={[<Head />, <OnBoardingPersonMeditate />]} />
        <Route path="/onboarding-completed" element={[<Head />, <OnBoardingCompleted />]} />
        <Route path="/onboarding-person-feeling-today" element={[<Head />, <OnboardingFeelToday />]} />
        <Route path="/terms-n-conditions" element={[<Head />, <TermsAndCondition />]} />
        <Route path="/privacy-policies" element={[<Head />, <PrivacyPolicy />]} />
        <Route
          path="/*"
          element={
            <HomeRouter
              getNotifications={getNotifications}
              isExistingSession={isExistingSession}
              isASessionAccepted={isASessionAccepted}
              setIsASessionAccepted={setIsASessionAccepted}
              acceptedSessionData={acceptedSessionData}
              acceptedSessionIds={acceptedSessionIds}
              isAcceptedModalOpen={isAcceptedModalOpen}
              setIsAcceptedModalOpen={setIsAcceptedModalOpen}
              isPaymentPending={isPaymentPending}
            />
          }
        />
      </Routes>
      {!isPaymentPending && acceptedSessionData.length > 0 && isAcceptedModalOpen && (
        <CreateSessionSuccessModal
          data={acceptedSessionData[0]}
          setIsModalOpen={setIsAcceptedModalOpen}
          id={acceptedSessionIds[0]}
          getHeaderNotifications={getNotifications}
        />
      )}
    </AuthModalContext.Provider>
  );
}

